class MiniProductForm {
  constructor(productHandle, variantId, options = {}) {
    this.drawer = null;
    this.drawerAnimation = null;
    this.productHandle = productHandle;
    this.variantId = variantId;
    this.activeElement = null; // Store the element that triggered the drawer for focus return

    this.options = Object.assign({
      onOpen: null,
      onClose: null,
    }, options);
  }

  async fetchAndOpenDrawer() {
    try {
      if (!this.drawer) {
        this.createDrawer();
      }

      // Fetch the mini-product-form section using the Section Rendering API
      const url = `/products/${this.productHandle}?section_id=mini-product-form&variant=${this.variantId}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to load product form: ${response.status}`);
      }

      const html = await response.text();

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      const sectionContent = tempDiv.querySelector('.shopify-section');
      if (sectionContent) {
        const drawerContent = this.drawer.querySelector('.product-modify-drawer__content');
        drawerContent.innerHTML = '';
        drawerContent.appendChild(sectionContent);

        this.initializeFormJS();
        this.openDrawer();
      } else {
        throw new Error('Product form section not found in response');
      }
    } catch (error) {
      console.error('Error loading product form:', error);
    }
  }

  createDrawer() {
    this.drawer = document.createElement('div');
    this.drawer.classList.add('product-modify-drawer');

    this.drawer.innerHTML = `
      <div class="product-modify-drawer__overlay"></div>
      <div
        class="product-modify-drawer__container"
        role="dialog"
        aria-modal="true"
        aria-label="Modify product"
        tabindex="-1"
      >
        <div class="product-modify-drawer__header">
          <h2 id="product-modify-drawer-title">Modify</h2>
          <button class="product-modify-drawer__close" aria-label="Close modify drawer">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
              class="icon icon-close"
              fill="none"
              viewBox="0 0 15 15"
            >
              <path fill-rule="evenodd" clip-rule="evenodd" d="M14.1908 0.706913L0.706913 14.1908L0 13.4838L13.4838 0L14.1908 0.706913Z" fill="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M0.706913 0L14.1908 13.4838L13.4838 14.1908L0 0.706913L0.706913 0Z" fill="currentColor"/>
            </svg>
          </button>
        </div>
        <div class="product-modify-drawer__content"></div>
      </div>
    `;

    // Add aria-labelledby to reference the title
    const container = this.drawer.querySelector('.product-modify-drawer__container');
    container.setAttribute('aria-labelledby', 'product-modify-drawer-title');

    const overlay = this.drawer.querySelector('.product-modify-drawer__overlay');
    const closeButton = this.drawer.querySelector('.product-modify-drawer__close');

    overlay.addEventListener('click', this.closeDrawer.bind(this));
    closeButton.addEventListener('click', this.closeDrawer.bind(this));

    // Store escape handler so we can remove it later if needed
    this.escapeHandler = (event) => {
      if (event.key === 'Escape' && this.isDrawerOpen()) {
        event.stopPropagation(); // Prevent cart drawer from also closing
        this.closeDrawer();
      }
    };

    document.addEventListener('keyup', this.escapeHandler);

    document.body.appendChild(this.drawer);

    this.drawerAnimation = new DrawerAnimation(
      this.drawer,
      this.drawer.querySelector('.product-modify-drawer__container'),
      {
        type: 'drawer',
        drawerPosition: 'right',
        onOpen: () => {
          this.trapFocus();
        },
        onClose: () => {
          document.body.classList.remove('overflow-hidden');
          this.removeTrapFocus();
          this.resetDrawer();
        }
      }
    );
    
    this.addStyles();
  }

  addStyles() {
    // Only add styles once
    if (document.getElementById('product-modify-drawer-styles')) return;

    const styles = document.createElement('style');
    styles.id = 'product-modify-drawer-styles';
    styles.textContent = `
      .product-modify-drawer {
        --modify-drawer-header-height: 6rem;

        visibility: hidden;
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 999;
        overflow: hidden;
      }
      
      .product-modify-drawer.is-open,
      .product-modify-drawer[open] {
        visibility: visible;
      }
      
      .product-modify-drawer__overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(0px);
      }
      
      .product-modify-drawer__container {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        width: 95vw;
        max-width: 46rem;
        background-color: #fff;
        display: flex;
        flex-direction: column;
        box-shadow: 0 0 25px rgba(0, 0, 0, 0.15);
        z-index: 1;
        will-change: transform;
      }
      
      .product-modify-drawer__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px;
        border-bottom: 1px solid #EAEAEA;
      }
      
      .product-modify-drawer__header h2 {
        margin: 0;
        color: #0A0A0A;
        font-family: "GT America";
        font-size: 14px;
        font-style: normal;
        font-weight: 300;
        line-height: 18px;
        letter-spacing: 1.6px;
        text-transform: uppercase;
      }
      
      .product-modify-drawer__close {
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
      }

      .product-modify-drawer__close .icon {
        width: 14px;
        height: 14px;
        display: block;
        color: #0A0A0A;
      }

      .product-modify-drawer__close:focus {
        outline: 2px solid #0A0A0A;
        outline-offset: 2px;
      }
      
      .product-modify-drawer__content {
        height: calc(100% - var(--modify-drawer-header-height));
      }

      /* Ensure focus is visible for all interactive elements in the drawer */
      .product-modify-drawer button:focus,
      .product-modify-drawer a:focus,
      .product-modify-drawer input:focus,
      .product-modify-drawer select:focus,
      .product-modify-drawer textarea:focus,
      .product-modify-drawer [tabindex]:not([tabindex="-1"]):focus {
        outline: 2px solid #0A0A0A;
        outline-offset: 2px;
      }

      /* Ensure keyboard focus is always visible (override :focus-visible) */
      .product-modify-drawer button:focus-visible,
      .product-modify-drawer a:focus-visible,
      .product-modify-drawer input:focus-visible,
      .product-modify-drawer select:focus-visible,
      .product-modify-drawer textarea:focus-visible,
      .product-modify-drawer [tabindex]:not([tabindex="-1"]):focus-visible {
        outline: 2px solid #0A0A0A;
        outline-offset: 2px;
      }
      
      @media (max-width: 639px) {
        .product-modify-drawer__container {
          max-width: 100%;
        }
      }
    `;

    document.head.appendChild(styles);
  }

  openDrawer() {
    // Store the currently focused element to return focus when closing
    this.activeElement = document.activeElement;

    if (this.drawerAnimation) {
      this.drawerAnimation.open();
    } else {
      // Fallback if animation isn't initialized for some reason
      this.drawer.classList.add('is-open');
      document.body.classList.add('overflow-hidden');
      this.trapFocus();
    }

    if (this.options.onOpen) {
      this.options.onOpen(this.drawer);
    }
  }

  closeDrawer() {
    if (this.drawerAnimation) {
      this.drawerAnimation.close();
    } else {
      // Fallback if animation isn't initialized for some reason
      this.drawer?.classList.remove('is-open');
      document.body.classList.remove('overflow-hidden');
      this.removeTrapFocus();
      this.resetDrawer();
    }

    if (this.options.onClose) {
      this.options.onClose();
    }
  }

  isDrawerOpen() {
    return this.drawer && (this.drawer.classList.contains('is-open') || this.drawer.hasAttribute('open'));
  }

  trapFocus() {
    // Use the global trapFocus function for proper focus trapping
    const container = this.drawer.querySelector('.product-modify-drawer__container');
    const closeButton = this.drawer.querySelector('.product-modify-drawer__close');

    // Wait for the 300ms slide-in transition to complete before setting focus
    setTimeout(() => {
      if (typeof window.trapFocus === 'function') {
        // Use global trapFocus, focus the close button
        window.trapFocus(container, closeButton);
      } else {
        // Fallback if global trapFocus isn't available
        if (closeButton) {
          closeButton.focus();
        }
      }
      
      // Announce to screen readers that the modify drawer has opened
      this.announceDrawerOpen();
    }, 300);
  }

  removeTrapFocus() {
    // Use the global removeTrapFocus function and return focus to trigger element
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(this.activeElement);
    } else if (this.activeElement) {
      // Fallback if global removeTrapFocus isn't available
      this.activeElement.focus();
    }
    
    // Announce to screen readers that the drawer has closed
    this.announceDrawerClose();
  }

  announceDrawerOpen() {
    // Create or update a live region for screen reader announcements
    this.announce('Modify product drawer opened. You can now change product options.');
  }

  announceDrawerClose() {
    this.announce('Modify product drawer closed.');
  }

  announce(message) {
    let announcer = document.getElementById('modify-drawer-announcer');
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'modify-drawer-announcer';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.cssText = 'position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0;';
      document.body.appendChild(announcer);
    }
    // Clear and set message to trigger announcement
    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  initializeFormJS() {
    // Re-init variant selectors if they exist
    const variantSelects = this.drawer.querySelectorAll('variant-selects, variant-radios');
    if (variantSelects.length > 0) {
      variantSelects.forEach(select => {
        if (typeof select.updateOptions === 'function') {
          select.updateOptions();
        }
      });
    }

    const quantityInputs = this.drawer.querySelectorAll('quantity-input');
    if (quantityInputs.length > 0) {
      quantityInputs.forEach(input => {
        if (input.querySelector('input') && this.quantity) {
          // Set the quantity to match the cart item's quantity
          const quantityInput = input.querySelector('input');
          quantityInput.value = this.quantity;

          const quantityDisplays = input.querySelectorAll('.quantity-display');
          if (quantityDisplays.length) {
            quantityDisplays.forEach(display => {
              display.textContent = this.quantity;
            });
          }
        }
      });
    }

    // Reinitialize inline scripts
    const scripts = this.drawer.querySelectorAll('script');
    scripts.forEach(script => {
      const newScript = document.createElement('script');

      Array.from(script.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });

      if (script.textContent) {
        newScript.textContent = script.textContent;
      }

      if (script.src) {
        document.head.appendChild(newScript);
        script.remove();
      } else {
        script.parentNode.replaceChild(newScript, script);
      }
    });
  }

  resetDrawer() {
    // Remove escape key handler
    if (this.escapeHandler) {
      document.removeEventListener('keyup', this.escapeHandler);
      this.escapeHandler = null;
    }
    
    this.drawer?.remove();
    this.drawer = null;
    this.drawerAnimation = null;
  }
}